import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, X } from "lucide-react";

const WaMark = ({ size = 16 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M17.47 14.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.95 1.16-.17.2-.35.22-.65.07-.3-.15-1.13-.42-2.15-1.33-.79-.71-1.33-1.58-1.48-1.88-.15-.3-.02-.46.13-.61.15-.15.35-.42.52-.62.13-.15.2-.27.3-.47.1-.2.05-.37-.02-.52-.08-.15-.67-1.6-.92-2.19-.24-.58-.49-.5-.67-.5h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.01-1.04 2.46 0 1.46 1.06 2.86 1.21 3.06.15.2 2.1 3.2 5.08 4.37 2.98 1.16 3.32.98 3.92.93.6-.05 1.94-.79 2.21-1.55.27-.77.27-1.43.2-1.56-.08-.13-.28-.2-.58-.35zM12.04 21.5h-.01a9.42 9.42 0 01-4.8-1.31l-.34-.2-3.57.93.96-3.48-.23-.36a9.4 9.4 0 01-1.44-5.02c0-5.2 4.24-9.44 9.45-9.44 2.52 0 4.89.99 6.67 2.77a9.37 9.37 0 012.76 6.68c0 5.2-4.24 9.43-9.45 9.43zM20.5 3.49A11.32 11.32 0 0012.04 0C5.79 0 .71 5.08.71 11.33c0 2 .52 3.95 1.52 5.67L0 24l7.17-1.88a11.3 11.3 0 004.86 1.11h.01c6.25 0 11.33-5.08 11.33-11.33 0-3.03-1.18-5.87-3.32-8.01z" />
  </svg>
);

export const WA_REASONS = [
  { id: "doubt", label: "I have a doubt before registration" },
  { id: "later", label: "I want to pay fees in a few hours" },
];

/** Host-controlled "WhatsApp us" button on the student's meeting screen.
 *  Name + reason are captured here so the chat opens already written. */
export function WhatsappUs({ roomName, userName, whatsapp, preview = false }) {
  const [open, setOpen] = useState(false);
  const [number, setNumber] = useState("");
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    api
      .get(`/meetings/${roomName}/pay/config`)
      .then(({ data }) => setNumber(data?.whatsapp_number || ""))
      .catch(() => {});
  }, [roomName]);

  const ready = name.trim().length >= 2 && !!reason;

  const send = () => {
    const who = name.trim();
    if (who.length < 2) return toast.error("Please type your full name");
    if (!number) return toast.error("WhatsApp isn't set up for this class yet");
    const label = WA_REASONS.find((r) => r.id === reason)?.label || "";
    if (!label) return toast.error("Please pick a reason");
    const text = `I am ${who}. ${label}.`;
    // A host preview must never pollute the real lead list.
    if (!preview)
      api
        .post(`/meetings/${roomName}/wa-intent`, {
          user_name: who,
          reason,
          whatsapp: whatsapp || null,
        })
        .catch(() => {});
    window.open(`https://wa.me/91${number}?text=${encodeURIComponent(text)}`, "_blank", "noopener");
    setOpen(false);
    toast.success(preview ? "This is what students get — WhatsApp is open" : "WhatsApp is open — just hit send");
  };

  return (
    <>
      <motion.button
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: "spring", stiffness: 320, damping: 22 }}
        onClick={() => setOpen(true)}
        className="fixed right-4 bottom-[104px] z-[70] h-12 pl-3 pr-4 rounded-full bg-[#25D366] text-white font-semibold text-[13px] flex items-center gap-2 shadow-[0_12px_30px_-8px_rgba(37,211,102,0.75)] hover:brightness-105 active:scale-95 transition-all"
        data-testid="wa-us-button"
      >
        <span className="w-8 h-8 rounded-full bg-white/20 grid place-items-center">
          <WaMark size={18} />
        </span>
        WhatsApp us
        {preview && (
          <span className="ml-1 px-1.5 py-0.5 rounded-md bg-black/25 text-[9.5px] uppercase tracking-[0.12em] font-bold">
            Preview
          </span>
        )}
      </motion.button>

      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-[96] bg-black/70 backdrop-blur-[2px] grid place-items-center p-4"
          data-testid="wa-us-modal"
          role="dialog"
          aria-modal="true"
        >
          <motion.div
            initial={{ y: 22, scale: 0.98, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            transition={{ type: "spring", damping: 26, stiffness: 320 }}
            className="w-full max-w-[420px] rounded-[20px] bg-white p-5 shadow-[0_30px_90px_-20px_rgba(0,0,0,0.8)]"
          >
            <div className="flex items-center gap-3">
              <span className="w-12 h-12 shrink-0 rounded-full bg-[#25D366] text-white grid place-items-center">
                <WaMark size={24} />
              </span>
              <div className="flex-1 leading-tight">
                <div className="text-[21px] font-bold text-neutral-900 leading-none">Message us on WhatsApp</div>
                {preview && (
                  <div className="text-[12.5px] text-neutral-500">
                    Host preview — exactly what your students see.
                  </div>
                )}
              </div>
              <button
                onClick={() => setOpen(false)}
                className="h-8 w-8 grid place-items-center rounded-full hover:bg-neutral-100"
                data-testid="wa-us-close"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-neutral-900" />
              </button>
            </div>

            <label className="block mt-4 text-[12.5px] font-bold text-neutral-900 uppercase tracking-wide">
              Your full name
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 60))}
              placeholder="Enter your full name"
              className="mt-1.5 w-full h-12 px-4 rounded-[12px] border border-neutral-900/70 focus:border-[#25D366] text-[14px] text-neutral-900 outline-none transition-colors"
              data-testid="wa-us-name"
            />

            <label className="block mt-3.5 text-[12.5px] font-bold text-neutral-900 uppercase tracking-wide">
              Select a reason
            </label>
            <div className="relative mt-1.5">
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className={`w-full h-12 pl-4 pr-10 rounded-[12px] border border-neutral-900/70 focus:border-[#25D366] text-[14px] bg-white outline-none appearance-none transition-colors ${reason ? "text-neutral-900" : "text-neutral-400"}`}
                data-testid="wa-us-reason"
              >
                <option value="" disabled>
                  Select a reason
                </option>
                {WA_REASONS.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="w-5 h-5 text-neutral-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>

            {ready && (
              <div className="mt-3.5 px-4 py-3 rounded-[12px] bg-[#f1f7f2] border border-[#dce9de] text-[13px] text-neutral-700">
                “I am <span className="font-semibold">{name.trim()}</span>.{" "}
                {WA_REASONS.find((r) => r.id === reason)?.label}.”
              </div>
            )}
            <button
              onClick={send}
              disabled={!ready}
              className={`mt-4 w-full h-14 rounded-[12px] bg-[#25D366] text-white font-bold text-[14px] flex items-center justify-center gap-2 transition-all ${
                ready
                  ? "opacity-100 hover:brightness-105 active:scale-[0.99]"
                  : "opacity-30 cursor-not-allowed"
              }`}
              data-testid="wa-us-send"
            >
              <WaMark size={19} /> Message
            </button>
            {!ready && (
              <div className="mt-2 text-center text-[12px] text-neutral-400" data-testid="wa-us-hint">
                Type your name and pick a reason to continue
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </>
  );
}
